import { supabase, q } from '../lib/supabase.js';
import { competenciaCurta } from '../lib/supabase.js';
import * as Demo from '../demo/demo.js';

/**
 * Substitui docMatrixFor() do protótipo, que gerava a matriz por regras
 * chumbadas no código ("se empresa == novorumo e banco == Sicoob então
 * vermelho"). Agora cada célula é um registro de verdade, editável.
 */

export const STATUS_DOC = {
  recebido: { cor: 'var(--green)', rotulo: 'Recebido' },
  faltante: { cor: 'var(--red)', rotulo: 'Faltante' },
  analise: { cor: 'var(--amber)', rotulo: 'Em análise' },
  parcial: { cor: 'var(--purple)', rotulo: 'Parcial' },
};

/** Últimas N competências, mais antiga primeiro (cabeçalho da matriz). */
export function ultimasCompetencias(n = 7, ate = new Date()) {
  const lista = [];
  const d = new Date(ate.getFullYear(), ate.getMonth(), 1);
  for (let i = n - 1; i >= 0; i--) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    lista.push({
      iso: `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}-01`,
      curta: competenciaCurta(`${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}-01`),
    });
  }
  return lista;
}

/**
 * Monta a matriz banco × competência de uma empresa.
 * Célula sem registro no banco é tratada como 'faltante' — que é a
 * situação real: se ninguém marcou que chegou, não chegou.
 */
export async function matriz(empresa, competencias) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    return (empresa.bancos || []).map((banco) => ({
      banco: { ...banco, ...(Demo.regraDoBanco(banco) || {}) },
      celulas: competencias.map((c) => {
        const reg = Demo.documentos.find((d) => d.banco_id === banco.id && d.competencia === c.iso);
        return { competencia: c.iso, bancoId: banco.id, status: reg?.status ?? 'faltante', obs: reg?.obs ?? null, existe: !!reg };
      }),
    }));
  }

  const isos = competencias.map((c) => c.iso);

  const registros = await q(
    supabase
      .from('documentos_bancarios')
      .select('id, banco_id, competencia, status, obs')
      .eq('empresa_id', empresa.id)
      .in('competencia', isos)
  );

  const chave = (bancoId, iso) => `${bancoId}|${iso}`;
  const porChave = new Map(registros.map((r) => [chave(r.banco_id, r.competencia), r]));

  // A regra da instituição vem junto: o colaborador precisa dela na hora
  // de marcar o extrato, não depois.
  const regras = await q(
    supabase.from('v_contas_com_regra').select('banco_id, regra, severidade').eq('empresa_id', empresa.id)
  );
  const regraPorBanco = new Map(regras.map((r) => [r.banco_id, r]));

  return (empresa.bancos || []).map((banco) => ({
    banco: { ...banco, ...(regraPorBanco.get(banco.id) || {}) },
    celulas: competencias.map((c) => {
      const reg = porChave.get(chave(banco.id, c.iso));
      return {
        competencia: c.iso,
        bancoId: banco.id,
        status: reg?.status ?? 'faltante',
        obs: reg?.obs ?? null,
        existe: !!reg,
      };
    }),
  }));
}

/** Clicar na bolinha grava de verdade. A trigger registra no histórico. */
export async function definirStatus({ empresaId, bancoId, competencia, status, obs = null, exigenciaCumprida = null }, colaboradorId) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    let reg = Demo.documentos.find((d) => d.banco_id === bancoId && d.competencia === competencia);
    const anterior = reg?.status ?? null;
    if (reg) { reg.status = status; reg.obs = obs; }
    else { reg = { id: 'd-demo-' + Date.now(), empresa_id: empresaId, banco_id: bancoId, competencia, status, obs }; Demo.documentos.push(reg); }
    const empresa = Demo.empresas.find((e) => e.id === empresaId);
    const banco = empresa?.bancos.find((b) => b.id === bancoId);
    Demo.registrarHistorico({ empresaId, fechamentoId: null, competencia, tipo: 'documento',
      descricao: 'Extrato ' + (banco?.nome ?? '?'), anterior, novo: status });
    return reg;
  }

  const [linha] = await q(
    supabase
      .from('documentos_bancarios')
      .upsert(
        {
          empresa_id: empresaId,
          banco_id: bancoId,
          competencia,
          status,
          obs,
          exigencia_cumprida: exigenciaCumprida,
          atualizado_por: colaboradorId,
        },
        { onConflict: 'banco_id,competencia' }
      )
      .select()
  );
  return linha;
}

/** Contagens para os cards do painel. */
export async function contagens(competencias) {
  const isos = competencias.map((c) => c.iso);
  const registros = await q(
    supabase.from('documentos_bancarios').select('status').in('competencia', isos)
  );

  return registros.reduce(
    (acc, r) => ({ ...acc, [r.status]: (acc[r.status] || 0) + 1 }),
    { recebido: 0, faltante: 0, analise: 0, parcial: 0 }
  );
}

/**
 * Quais contas já tiveram o documento marcado como recebido no mês.
 *
 * Existe porque o cartão DOCUMENTOS do painel não olhava documento
 * nenhum: ele contava contas bancárias menos itens de conferência
 * pendentes, e mostrava "0 faltantes, 13 recebidos" com zero documentos
 * registrados no banco. Número errado é pior que botão morto — a pessoa
 * acredita nele.
 */
export async function recebidosNoMes(competencia) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    return Demo.documentos
      .filter((d) => d.competencia === competencia && d.status === 'recebido')
      .map((d) => ({ banco_id: d.banco_id }));
  }

  return q(
    supabase
      .from('documentos_bancarios')
      .select('banco_id')
      .eq('competencia', competencia)
      .eq('status', 'recebido')
  );
}

/**
 * Situação de todas as contas num mês — a base da tela virada para o mês.
 *
 * A tela antiga era por empresa: escolhia uma no seletor e via os bancos
 * dela mês a mês. Mas o trabalho do dia 5 não é por empresa, é por mês —
 * "quem ainda não mandou o extrato de agosto?" — e responder isso exigia
 * abrir 124 empresas uma a uma.
 *
 * Célula sem registro é faltante: se ninguém marcou que chegou, não chegou.
 */
export async function statusDoMes(competencia) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    return Demo.documentos
      .filter((d) => d.competencia === competencia)
      .map((d) => ({ banco_id: d.banco_id, status: d.status, obs: d.obs ?? null }));
  }

  return q(
    supabase
      .from('documentos_bancarios')
      .select('banco_id, status, obs')
      .eq('competencia', competencia)
  );
}

/**
 * Regras por instituição, de todas as contas de uma vez.
 *
 * Antes isso era buscado por empresa, porque a tela era por empresa. Com
 * a tela virada para o mês, a lista traz contas de 124 empresas — uma
 * consulta por empresa seriam 124 idas ao banco a cada abertura.
 *
 * A regra que existe hoje: no Itaú só vale o PDF consolidado.
 */
export async function regrasDeTodas() {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    const mapa = new Map();
    for (const e of Demo.empresas) {
      for (const b of e.bancos || []) {
        const r = Demo.regraDoBanco(b);
        if (r) mapa.set(b.id, r);
      }
    }
    return mapa;
  }

  const linhas = await q(
    supabase.from('v_contas_com_regra').select('banco_id, regra, severidade').not('regra', 'is', null)
  );
  return new Map(linhas.map((r) => [r.banco_id, r]));
}
